import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import CorpInquiryForm from './CorpInquiryForm';
import CorpLabelShowcase from './corp/CorpLabelShowcase';
import CorpChristmas from './corp/CorpChristmas';
import CorpPartnerSection from './corp/CorpPartnerSection';
import CorpUseCases from './corp/CorpUseCases';
import CorpShowcase from './corp/CorpShowcase';
import CorpMakeItYours from './corp/CorpMakeItYours';
import CorpCanadian from './corp/CorpCanadian';
import { trackCta } from './tracking';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INDUSTRIES = [
  { id: 'real_estate', title: 'Real Estate', tagline: 'Closing gifts they open. Referrals that come back.',
    uses: ['Closing gifts', 'Open houses', 'Client anniversaries', 'Referral gifts', 'Brokerage events', 'New homeowner gifts', 'Presentation centres'] },
  { id: 'financial', title: 'Financial & Professional Services', tagline: 'Say thank-you like you mean it.',
    uses: ['Client appreciation', 'Holiday gifting', 'Referral partners', 'Milestone celebrations', 'VIP clients', 'Corporate events'] },
  { id: 'hospitality', title: 'Hospitality & Airbnb', tagline: 'A welcome bottle that becomes a review.',
    uses: ['Welcome bottles', 'Guest experiences', 'Branded house wine', 'Special occasions', 'VIP guests', 'Repeat-guest gifts'] },
  { id: 'construction', title: 'Construction & Trades', tagline: 'Hand over the keys with something to celebrate.',
    uses: ['Project completion gifts', 'Homeowner handovers', 'Supplier appreciation', 'Employee recognition', 'Holiday gifts'] },
  { id: 'wedding_event', title: 'Weddings & Events', tagline: 'Table wine that looks like your day.',
    uses: ['Custom wedding labels', 'Table wine', 'Wedding party gifts', 'Guest favours', 'Anniversary bottles', 'Event-branded wine'] },
  { id: 'corporate', title: 'Businesses', tagline: 'A gift your team and clients keep talking about.',
    uses: ['Client gifts', 'Employee recognition', 'Holiday gifting', 'Product launches', 'Grand openings', 'Milestones', 'Corporate events'] },
];

const REPEAT_PROMPTS = [
  'Closing next week?',
  'Need another client gift?',
  'Another Airbnb guest arriving?',
  'Hosting another event?',
  'Christmas gifts for the team?',
];

const outlineBtn = { background: 'transparent', color: 'var(--ff-cream)', border: '2px solid rgba(245,235,220,0.55)' };

const CampaignLanding = ({ basePath = '' }) => {
  const navigate = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [corpCfg, setCorpCfg] = useState(null);

  useEffect(() => {
    axios.get(`${API}/campaign/config`).then((r) => setCfg(r.data)).catch(() => {});
    axios.get(`${API}/corp/config`).then((r) => setCorpCfg(r.data)).catch(() => {});
  }, []);

  const discount = cfg?.discount ?? 15;
  const christmasOn = corpCfg?.christmasEnabled !== false;

  // Two conversion paths, tracked separately
  const goOrder = (ctaId) => { trackCta('order', ctaId); navigate(`${basePath}/order`); };
  const goPartner = (ctaId) => {
    trackCta('partner', ctaId);
    document.getElementById('corp-inquiry')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <>
      <section className="ff-hero" data-testid="corp-hero">
        <div className="ff-container">
          <p className="ff-eyebrow">Custom Wine for Business, Gifting & Celebrations</p>
          <h1>Your Wine. Your Brand. Your Moment.</h1>
          <p>
            Create custom wine for the people who matter to your business. Whether you're
            thanking a client, celebrating a closing, welcoming a guest, recognizing your team
            or preparing for the holidays, The Lokal makes it easy to turn a bottle of wine
            into something personal.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'center', marginTop: 24 }}>
            <button className="ff-btn ff-btn-gold ff-btn-lg" data-testid="corp-hero-create" onClick={() => goOrder('hero_create')}>
              Create Your Corporate Wine
            </button>
            <button className="ff-btn ff-btn-lg" data-testid="corp-hero-partner" onClick={() => goPartner('hero_partner')} style={outlineBtn}>
              Become a Corporate Partner →
            </button>
          </div>
          <p style={{ marginTop: 20, fontSize: 14, opacity: 0.85 }} data-testid="corp-hero-savings">
            <strong>Corporate Partners Save {discount}%</strong> on their wine order.
          </p>
        </div>
      </section>

      {christmasOn && <CorpChristmas onOrder={() => goOrder('christmas_order')} />}

      <CorpMakeItYours onOrder={goOrder} onExplore={() => document.getElementById('corp-labels')?.scrollIntoView({ behavior: 'smooth' })} />

      <section className="ff-section" data-testid="corp-offer">
        <div className="ff-container ff-form-shell ff-text-center">
          <p className="ff-eyebrow" style={{ color: 'var(--ff-gold)' }}>{discount}% CORPORATE PARTNER SAVINGS</p>
          <h2>{christmasOn ? 'Planning Your Holiday Gifts? Start Here.' : `Corporate Partners Save ${discount}%.`}</h2>
          <p className="ff-lede">
            Approved corporate orders receive {discount}% off their wine order. The wine is the reason
            people say <em>thank you</em> — the savings just make saying yes a little easier.
          </p>
          <button className="ff-btn ff-btn-primary ff-btn-lg" data-testid="corp-offer-cta" onClick={() => goOrder('offer_claim')} style={{ marginTop: 12 }}>
            Start Your Order
          </button>
        </div>
      </section>

      <CorpCanadian />

      <CorpShowcase onOrder={goOrder} onPartner={goPartner} />

      <div id="corp-labels"><CorpLabelShowcase /></div>

      <section className="ff-section" data-testid="corp-audience">
        <div className="ff-container">
          <p className="ff-eyebrow">WHO IT'S FOR</p>
          <h2>Made for Business. Made for Celebrations. Made for You.</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20, marginTop: 32 }}>
            {INDUSTRIES.map((ind) => (
              <article key={ind.id} className="ff-card" data-testid={`corp-audience-${ind.id}`} style={{ padding: 22 }}>
                <p style={{ margin: 0, fontSize: 11, letterSpacing: 2, fontWeight: 700, color: 'var(--ff-gold)' }}>
                  {ind.title.toUpperCase()}
                </p>
                <h3 style={{ margin: '6px 0 10px', fontSize: 20, color: 'var(--ff-burgundy)' }}>{ind.tagline}</h3>
                <ul style={{ margin: 0, paddingLeft: 18, color: 'var(--ff-brown-soft)', fontSize: 14 }}>
                  {ind.uses.map((u) => <li key={u} style={{ marginBottom: 4 }}>{u}</li>)}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="ff-section ff-section-alt" data-testid="corp-how">
        <div className="ff-container">
          <p className="ff-eyebrow">HOW IT WORKS</p>
          <h2>Three Steps to Wine They'll Actually Remember.</h2>
          <div className="ff-steps" style={{ marginTop: 28 }}>
            <div className="ff-step" data-testid="corp-step-1">
              <div className="ff-step-num">1</div>
              <h4>Choose Your Wine</h4>
              <p>Select a wine that fits your taste, occasion and budget.</p>
            </div>
            <div className="ff-step" data-testid="corp-step-2">
              <div className="ff-step-num">2</div>
              <h4>Make It Yours</h4>
              <p>Create a custom label featuring your brand, artwork, message or event.</p>
            </div>
            <div className="ff-step" data-testid="corp-step-3">
              <div className="ff-step-num">3</div>
              <h4>Share Something Memorable</h4>
              <p>Use your wine for gifting, clients, events, celebrations or hospitality.</p>
            </div>
          </div>
          <div className="ff-text-center" style={{ marginTop: 32 }}>
            <button className="ff-btn ff-btn-primary ff-btn-lg" data-testid="corp-how-cta" onClick={() => goOrder('how_order')}>
              Create Your Corporate Wine
            </button>
          </div>
        </div>
      </section>

      <CorpUseCases />

      <section className="ff-section ff-section-alt" data-testid="corp-repeat">
        <div className="ff-container ff-form-shell ff-text-center">
          <p className="ff-eyebrow">FOR BUSINESSES THAT COME BACK</p>
          <h2>Create It Once. Order It Again.</h2>
          <p className="ff-lede">
            Set up your custom label and design once. Then come back whenever you need
            more — your wine program is already established, so reordering takes minutes.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 10, marginTop: 24 }}>
            {REPEAT_PROMPTS.map((q) => (
              <span key={q} style={{
                padding: '10px 16px', background: 'var(--ff-cream)', borderRadius: 8,
                fontSize: 14, color: 'var(--ff-brown)', fontStyle: 'italic',
              }}>{q}</span>
            ))}
          </div>
          <p style={{ marginTop: 20, fontSize: 15, color: 'var(--ff-brown-soft)' }}>
            The Lokal becomes your ongoing gifting and relationship partner — not a one-time purchase.
          </p>
        </div>
      </section>

      <CorpPartnerSection onPartner={() => goPartner('partner_section')} onPlanning={() => goPartner('partner_planning')} />

      <section id="corp-inquiry" className="ff-section" data-testid="corp-inquiry-section">
        <div className="ff-container ff-form-shell">
          <div className="ff-text-center" style={{ marginBottom: 24 }}>
            <p className="ff-eyebrow">TELL US WHAT YOU'RE PLANNING</p>
            <h2>Let's Create Your Wine.</h2>
            <p className="ff-lede" style={{ maxWidth: 640, margin: '10px auto 0' }}>
              Whether you have a plan or just an idea — tell us a little about it and
              someone from The Lokal will be in touch to help bring it to life.
            </p>
          </div>
          <CorpInquiryForm />
        </div>
      </section>

      <section className="ff-section" data-testid="corp-final" style={{ background: 'var(--ff-burgundy)', color: 'var(--ff-cream)' }}>
        <div className="ff-container ff-text-center">
          <h2 style={{ color: 'var(--ff-cream)' }}>Ready to Put Your Brand on the Bottle?</h2>
          <p style={{ color: 'var(--ff-cream-light)', opacity: 0.9, maxWidth: 640, margin: '0 auto' }}>
            Save {discount}% through The Lokal Corporate Program and create custom wine your clients,
            employees, guests and partners will actually remember.
          </p>
          <div className="ff-mt-6" style={{ display: 'inline-flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
            <button className="ff-btn ff-btn-gold ff-btn-lg" data-testid="corp-final-cta" onClick={() => goOrder('final_order')}>
              Create Your Corporate Wine
            </button>
            <button className="ff-btn ff-btn-lg" data-testid="corp-final-partner" onClick={() => goPartner('final_partner')} style={outlineBtn}>
              Become a Corporate Partner
            </button>
          </div>
          <p style={{ marginTop: 20, fontSize: 13, opacity: 0.8 }}>You must be 19+ to order wine. Please enjoy responsibly.</p>
        </div>
      </section>
    </>
  );
};

export default CampaignLanding;
